import { AxiosInstance } from 'axios';
import { ESIAService } from './ESIA/api';
import {
  APITokens,
  LoginHandler,
  LogoutHandler,
  PrivatePolicyLackHandler,
  TokenControllerSettings,
} from './types';
import { createInstance } from './service';
import { IPersistenceStorage } from './TokenStorage';

export class TokenController {
  private _refreshToken: string | undefined;
  private _accessToken: string | undefined;
  private _esiaService: ESIAService;
  private _privatePolicyLackHandlers: Set<PrivatePolicyLackHandler>;
  private _loginHandlers: Set<LoginHandler>;
  private _logoutHandlers: Set<LogoutHandler>;
  private _axios: AxiosInstance;
  private _privacyPolicyData?: APITokens;

  constructor(
    settings: TokenControllerSettings,
    private _persistenceStorage: IPersistenceStorage,
    private _refreshTokenKey: string = 'refresh-token',
  ) {
    // TODO: Возможно, потребуется отказаться от использования axios из-за отсутствия ошибки при post-запросе в react-native
    // Необходимо проверить работу кода на react-native. В случае необходимости заменить axios на нативный fetch
    this._axios = createInstance(settings.basePath);
    this._esiaService = new ESIAService(settings, this._persistenceStorage);
    this._privatePolicyLackHandlers = new Set();
    this._loginHandlers = new Set();
    this._logoutHandlers = new Set();
  }

  refresh = async () => {
    const refreshToken =
      this._refreshToken ?? this._persistenceStorage.get(this._refreshTokenKey);

    if (!refreshToken) return;

    const access = await this._getAccessToken(refreshToken);

    this._set(access, refreshToken);
  };

  getAccessToken = async () => {
    if (!this._accessToken && this._refreshToken) {
      await this.loginByRefreshToken(this._refreshToken);
    }

    return this._accessToken;
  };

  logout = async () => {
    this._refreshToken = undefined;
    this._accessToken = undefined;
    this._persistenceStorage.clear(this._refreshTokenKey);

    await this._callLogoutHandlers();
  };

  loginByRefreshToken = async (token?: string) => {
    const resultRefreshToken =
      token ?? this._persistenceStorage.get(this._refreshTokenKey);

    if (!resultRefreshToken) return;

    const access = await this._getAccessToken(resultRefreshToken);

    this._set(access, resultRefreshToken);

    await this._callLoginHandlers();
  };

  loginByUsername = async (username: string, password: string) => {
    const { access, refresh, is_privacy_policy_accepted } =
      await this._getTokensAndPrivacyPolicy(username, password);

    if (!is_privacy_policy_accepted) {
      this._privacyPolicyData = { access, refresh };

      this._privatePolicyLackHandlers.forEach((cb) => {
        cb();
      });
    } else {
      this._set(access, refresh);

      await this._callLoginHandlers();
    }
  };

  // TODO: Реализовать следующую структуру: `controller: { esia: { getLoginForm, login }, ya: { getLoginForm, login }, user: {login}}, refresh,  ...othersFields`
  getEsiaLoginForm = async (): Promise<string> => {
    return await this._esiaService.getEsiaLoginLink();
  };

  loginByEsia = async (
    url: string,
    redirectToLogoutURL: (link: string) => Promise<void> | void,
  ) => {
    await this._esiaService.handleEsiaLogin(
      url,
      async ({ access, refresh }) => {
        this._set(access, refresh);
        await this._callLoginHandlers();
      },
      (access, refresh) => {
        this._privacyPolicyData = { access, refresh };

        this._privatePolicyLackHandlers.forEach((cb) => {
          cb();
        });
      },
      redirectToLogoutURL,
    );
  };

  acceptPrivacyPolicy = async () => {
    if (!this._privacyPolicyData) {
      throw new Error('Не удалось получить данные для входа');
    }

    const { access, refresh } = this._privacyPolicyData;

    await this._axios.post(
      `accept-policy/`,
      { is_privacy_policy_accepted: true },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + access,
        },
      },
    );

    this._set(access, refresh);
    this._privacyPolicyData = undefined;

    await this._callLoginHandlers();
  };

  cancelPrivacyPolicy = () => {
    this._privacyPolicyData = undefined;
  };

  onPrivacyPolicyNotAgreed = (cb: PrivatePolicyLackHandler) => {
    this._privatePolicyLackHandlers.add(cb);
  };

  unPrivacyPolicyNotAgreed = (cb: PrivatePolicyLackHandler) => {
    this._privatePolicyLackHandlers.delete(cb);
  };

  subscribeOnLogin = (cb: LoginHandler) => {
    this._loginHandlers.add(cb);
  };

  unsubscribeOnLogin = (cb: LoginHandler) => {
    this._loginHandlers.delete(cb);
  };

  subscribeOnLogout = (cb: LogoutHandler) => {
    this._logoutHandlers.add(cb);
  };

  unsubscribeOnLogout = (cb: LogoutHandler) => {
    this._logoutHandlers.delete(cb);
  };

  private _callLogoutHandlers = async () => {
    this._logoutHandlers.forEach(async (cb) => {
      try {
        await cb();
      } catch (e) {
        const message =
          e instanceof Error
            ? e.message
            : 'Не удалось выполнить обработчик события выхода из аккаунта';

        console.error(message);
      }
    });
  };

  private _callLoginHandlers = async () => {
    this._loginHandlers.forEach(async (cb) => {
      try {
        await cb();
      } catch (e) {
        const message =
          e instanceof Error
            ? e.message
            : 'Не удалось выполнить обработчик события входа в аккаунт';

        console.error(message);
      }
    });
  };

  private _set = (access: string, refresh: string) => {
    this._accessToken = access;
    this._refreshToken = refresh;
    this._persistenceStorage.set(this._refreshTokenKey, refresh);
  };

  private async _getAccessToken(refresh: string): Promise<string> {
    const resp = await this._axios.post<{ access: string }>('token/refresh/', {
      refresh,
    });

    // @ts-ignore
    return resp.data.access;
  }

  private async _getTokensAndPrivacyPolicy(
    username: string,
    password: string,
  ): Promise<APITokens & { is_privacy_policy_accepted: boolean }> {
    const resp = await this._axios.post<
      APITokens & { is_privacy_policy_accepted: boolean }
    >('privacy-policy-token/', {
      username: username,
      password: password,
    });

    // TODO: Убрать ts-ignore после выноса пакета из open. И удаления переопределения namespace axios в текущем проекте
    // @ts-ignore
    const { refresh, access, is_privacy_policy_accepted } = resp.data;

    return { refresh, access, is_privacy_policy_accepted };
  }
}
